import { LOWERCASED_UUID_PATTERN, MAX_STRING_VALUE_LENGTH } from "./deployment-config.js";
import { z } from "zod";
//#region src/deployment-config.schemas.ts
/**
* Deployment Configuration — zod-backed value validation + the wire DTO schemas
* (Deployment Configuration v1). Companion to `deployment-config.ts` (which holds
* the pure types + registry + resolver). All zod lives here — the package
* convention is that `.schemas.ts` owns runtime validation (cf. branding,
* app-factory-ca).
*
* `validateDeploymentConfigValue` is the SINGLE value-validation gate the WS2
* write API calls before persisting a value (FR-8; security-audit door #1): the
* candidate is checked against its definition's `valueType` + enum / format /
* length bounds. A definition-level bug (an 'enum' key that declares no
* `enumValues`) THROWS (programmer error, not user input); a user-supplied bad
* value returns `{ ok: false, error }` (→ HTTP 400). Tested to 100% coverage in
* `__tests__/deployment-config.schemas.test.ts`.
*/
/**
* Build the zod schema that validates a VALUE against one definition.
* Module-private; exercised through `validateDeploymentConfigValue`. Throws
* (registry-integrity) when an 'enum' definition omits its `enumValues` — that
* is a malformed definition (impossible for a build-shipped registered key),
* not a user error.
*/
function buildValueSchema(def) {
	switch (def.valueType) {
		case "boolean": return z.boolean();
		case "number": return z.number().finite();
		case "enum": {
			const allowed = def.enumValues;
			if (!allowed || allowed.length === 0) throw new Error(`registry integrity: enum key '${def.key}' declares no enumValues`);
			return z.string().refine((v) => allowed.includes(v), { message: `must be one of: ${allowed.join(", ")}` });
		}
		case "string":
			if (def.format === "lowercased-uuid") return z.string().regex(LOWERCASED_UUID_PATTERN, "must be a lowercased UUID");
			return z.string().min(1).max(MAX_STRING_VALUE_LENGTH);
	}
}
/**
* Validate a candidate value against its definition (FR-8). Returns a
* discriminated result — it NEVER throws for a bad VALUE (a bad value is a 400,
* not a crash). `.value` on success is the parsed value (unchanged for these
* primitive types). Callers: WS2 write controller (before persisting).
*/
function validateDeploymentConfigValue(def, value) {
	const result = buildValueSchema(def).safeParse(value);
	if (result.success) return {
		ok: true,
		value: result.data
	};
	return {
		ok: false,
		error: result.error.issues[0].message
	};
}
/** The `default` / `effective` primitive union (boolean | string | number). */
const configPrimitiveSchema = z.union([
	z.boolean(),
	z.string(),
	z.number()
]);
/**
* One entry in the `GET /v1/deployment-config` response (DC-4). WS2 produces it;
* WS5 (CLI) may parse it with this schema. `stored` is `null` when unset;
* `effective` = stored ?? env(server) ?? default (`z.unknown()` admits null).
*/
const DeploymentConfigEntryDtoSchema = z.object({
	key: z.string(),
	valueType: z.enum([
		"boolean",
		"string",
		"number",
		"enum"
	]),
	configClass: z.enum(["topology", "runtime"]),
	scope: z.literal("deployment"),
	stored: z.unknown(),
	effective: z.unknown(),
	default: configPrimitiveSchema
}).strict();
/** The `GET /v1/deployment-config` response envelope. */
const DeploymentConfigListDtoSchema = z.object({ entries: z.array(DeploymentConfigEntryDtoSchema) }).strict();
/**
* One audit row in the `GET /v1/deployment-config/:key/history` response (DC-4;
* mirrors DC-1's `deployment_config_history` columns). `oldValue` is `null` on
* the first set (`z.unknown()` admits null); `setAt` is an ISO timestamp.
* Server-produced — WS5 (CLI) may parse it with this schema.
*/
const DeploymentConfigHistoryEntryDtoSchema = z.object({
	id: z.string(),
	key: z.string(),
	scope: z.literal("deployment"),
	oldValue: z.unknown(),
	newValue: z.unknown(),
	setBy: z.string(),
	setAt: z.string()
}).strict();
/** The `GET /v1/deployment-config/:key/history` response envelope. */
const DeploymentConfigHistoryDtoSchema = z.object({
	key: z.string(),
	entries: z.array(DeploymentConfigHistoryEntryDtoSchema)
}).strict();
//#endregion
export { DeploymentConfigEntryDtoSchema, DeploymentConfigHistoryDtoSchema, DeploymentConfigHistoryEntryDtoSchema, DeploymentConfigListDtoSchema, validateDeploymentConfigValue };

//# sourceMappingURL=deployment-config.schemas.js.map